import { createContext, useContext, useState, useMemo, useCallback } from 'react';

/**
 * CaseStudyChapterContext — lets the top-level tab strip publish which chapter
 * (a.k.a. tab / phase / step) is currently active so that other pieces of the
 * case-study chrome — most importantly the sticky reading-progress chip — can
 * label the reader's position.
 *
 * Shape: { chapter: { index, total, label }, setChapter, clearChapter }
 * Default: `null` chapter (i.e. no tabs — plain scroll page).
 */

const CaseStudyChapterContext = createContext({
    chapter: null,
    setChapter: () => {},
    clearChapter: () => {},
});

export const CaseStudyChapterProvider = ({ children }) => {
    const [chapter, setChapterState] = useState(null);

    const setChapter = useCallback((next) => {
        setChapterState((prev) => {
            if (
                prev &&
                next &&
                prev.index === next.index &&
                prev.total === next.total &&
                prev.label === next.label
            ) {
                return prev;
            }
            return next;
        });
    }, []);

    const clearChapter = useCallback(() => setChapterState(null), []);

    const value = useMemo(
        () => ({ chapter, setChapter, clearChapter }),
        [chapter, setChapter, clearChapter]
    );

    return (
        <CaseStudyChapterContext.Provider value={value}>
            {children}
        </CaseStudyChapterContext.Provider>
    );
};

export const useCaseStudyChapter = () => useContext(CaseStudyChapterContext);
